/** CrmRecordDealUpdates (Item D / D5, R6) — embeds the Deal-Updates workflow INSIDE the record
 * panel for deal_pipeline entries (the panel's `rtab='updates'` sub-view).
 *
 * It REUSES the D4-extracted <DealDetail> fed by the existing `useDealDetail` query + the `useDeals`
 * mutation hooks — no workflow logic is forked. That means everything carries over intact:
 *   - `review_suggestion_id` staleness (409 stale-card) — DealDetail echoes it on every mutation.
 *   - drift banners, the "⟳ aggregating…" pill + the 4s poll (both live in useDealDetail/DealDetail).
 *   - log / push / log-pass / dismiss / merge / custom-instructions.
 *   - the post-push success panel.
 * The Deal-Updates API stays the workflow authority for deal entries (R6) — the generic CRM
 * providers do NOT drive deal-entry workflow here.
 *
 * The next-pending conveyor is mapped onto the panel's ‹ › nav set (RecordNavSource): after a
 * log/push, "Next pending update" advances to the next record via `onNext`; "Back to list" closes
 * the panel to the grid via `onClose`.
 */
import { useDealDetail } from '../../hooks/useDeals';
import { DealDetail } from '../pages/DealsPage.DealDetail';
import styles from './crm.module.css';

export function CrmRecordDealUpdates({
  dealKey,
  onNext,
  onClose,
}: {
  dealKey: string;
  /** Advance to the next record in the panel's ‹ › nav set (the conveyor target). */
  onNext: () => void;
  /** Close the panel back to the grid. */
  onClose: () => void;
}) {
  const detail = useDealDetail(dealKey);

  if (detail.isLoading) {
    return <div className={styles.recordSkeletonNote}>Loading updates…</div>;
  }
  if (detail.isError || !detail.data) {
    return (
      <div className={styles.recordActionError} role="alert">
        Could not load the deal updates for this record.
      </div>
    );
  }

  return (
    <div className={styles.recordUpdatesBody}>
      <DealDetail
        key={detail.data.deal_key}
        payload={detail.data}
        onNextPending={() => onNext()}
        onBackToPipeline={onClose}
        backLabel="Back to list"
      />
    </div>
  );
}
